import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { StatusBadge, ZoomImage, usePoll, fmtUSDT, fmtINR, TelegramButton } from "@/components/common";
import PaymentConfirm from "@/components/PaymentConfirm";

const SEG_LABEL = { payment_sent: "Awaiting confirmation", customer_confirmed: "Confirmed", dispute: "Disputed", expired: "Expired — review" };

export default function OrderDetail() {
  const { orderId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [tg, setTg] = useState("");
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const load = () => api.get(`/customer/orders/${orderId}`).then((r) => setData(r.data)).catch(() => {});
  usePoll(() => { load(); api.get("/settings/public").then((r) => setTg(r.data.telegram_url)).catch(() => {}); }, 5000, [orderId]);

  if (!data) return <div className="text-slate-400">Loading order…</div>;
  const o = data.order;
  const openStatuses = ["Pending", "Under Review", "Payment Processing", "Partially Paid", "Pending Settlement"];
  const canRequestCancel = openStatuses.includes(o.status) && !(o.cancel_request && o.cancel_request.status === "requested");
  const partiallyPaid = ["Partially Paid", "Pending Settlement"].includes(o.status);

  const requestCancel = async () => {
    if (!cancelReason.trim()) return toast.error("Please enter a reason");
    try {
      await api.post(`/customer/orders/${orderId}/request-cancel`,
        { reason: cancelReason, request_type: partiallyPaid ? "remaining" : "stop" });
      toast.success("Cancellation requested — awaiting review"); setShowCancel(false); setCancelReason(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const pending = data.segments.filter((s) => s.status === "payment_sent");

  return (
    <div className="max-w-3xl space-y-6">
      <button onClick={() => nav("/app/orders")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Back to orders
      </button>

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-lg font-bold text-slate-900">{o.order_id}</div>
            <div className="text-sm text-slate-500 mt-1">{o.product_name} · created {new Date(o.created_at).toLocaleString()}</div>
          </div>
          <StatusBadge status={o.status} testid="order-detail-status" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-5 text-sm">
          <Info label="USDT amount" value={`${fmtUSDT(o.amount)} USDT`} />
          <Info label="Locked rate" value={`${fmtINR(o.rate)} / USDT (${o.rate_source})`} />
          <Info label="Expected INR" value={fmtINR(o.expected_inr)} />
          <Info label="Confirmed INR paid" value={fmtINR(o.paid_inr)} tone="green" />
          <Info label="Remaining INR" value={fmtINR(o.remaining_inr)} />
          <Info label="Settled USDT" value={`${fmtUSDT(o.settled_usdt)} USDT`} tone="green" />
          <Info label="Remaining held USDT" value={`${fmtUSDT(o.remaining_held_usdt)} USDT`} />
          <Info label="Excess INR deducted" value={fmtINR(o.excess_inr_deducted)} />
          {o.upi_id && <Info label="Payout UPI" value={`${o.upi_id}${o.upi_name ? " · " + o.upi_name : ""}`} />}
        </div>

        {o.bank_proof_file_id && (
          <div className="mt-5 border-t border-slate-200 pt-4">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Bank account verification screenshot</div>
            <ZoomImage fileId={o.bank_proof_file_id} className="max-h-56 w-auto rounded-lg border border-slate-200 bg-slate-50" testid="bank-proof-image" />
            <div className="text-xs text-amber-700 mt-2" data-testid="bank-proof-status">Submitted — pending staff review. Uploading does not automatically verify your account.</div>
          </div>
        )}

        {o.cancel_request && (
          <div className={`mt-4 text-sm rounded-xl p-3 ${o.cancel_request.status === "requested" ? "bg-amber-50 border border-amber-200 text-amber-800" : o.cancel_request.status === "approved" ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-slate-50 border border-slate-200 text-slate-600"}`} data-testid="cancel-request-status">
            Cancellation {o.cancel_request.status}
            {o.cancel_request.reason ? ` · ${o.cancel_request.reason}` : ""}
            {o.cancel_request.status === "approved" && ` · released ${fmtUSDT(o.cancel_request.released_usdt)} USDT`}
          </div>
        )}

        {canRequestCancel && (
          <div className="mt-5">
            {!showCancel ? (
              <button onClick={() => setShowCancel(true)} data-testid="order-request-cancel-button"
                className="px-4 py-2 rounded-xl border border-rose-300 text-rose-700 text-sm font-semibold hover:bg-rose-50 transition">
                {partiallyPaid ? "Cancel Remaining Order" : "Stop / Cancel Order"}
              </button>
            ) : (
              <div className="space-y-2">
                <textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={2} placeholder="Reason for cancellation (required)"
                  data-testid="cancel-reason-input" className="w-full px-3 py-2 rounded-xl border border-slate-300 outline-none text-sm" />
                <div className="flex gap-2">
                  <button onClick={requestCancel} data-testid="submit-cancel-request" className="px-4 py-2 rounded-xl bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700">Submit request</button>
                  <button onClick={() => setShowCancel(false)} className="px-4 py-2 rounded-xl border border-slate-300 text-slate-600 text-sm">Cancel</button>
                </div>
                <p className="text-xs text-slate-400">Held USDT is not released until staff approve. Only the eligible unsold amount is released.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {pending.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-heading font-semibold text-slate-900">Action required</h2>
          {pending.map((s) => <PaymentConfirm key={s.segment_id} segment={s} orderId={o.order_id} onDone={load} />)}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-semibold text-slate-900">Payout segments</h2>
          <TelegramButton url={tg} />
        </div>
        {data.segments.length === 0 ? <div className="text-sm text-slate-400 py-6 text-center">No payouts recorded yet.</div> : (
          <div className="space-y-4" data-testid="order-segments-list">
            {data.segments.map((s) => (
              <div key={s.segment_id} className="border border-slate-200 rounded-xl p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-mono text-sm text-slate-900">{s.segment_id}</div>
                    <div className="text-sm text-slate-600">{fmtINR(s.amount)} · {s.method} · UTR {s.reference}</div>
                    <div className="text-xs text-slate-400 mt-0.5">Sent {s.payment_sent_at ? new Date(s.payment_sent_at).toLocaleString() : "—"}</div>
                    {s.dispute_reason && <div className="text-xs text-rose-600 mt-1">Dispute: {s.dispute_reason}</div>}
                  </div>
                  <StatusBadge status={s.status} />
                </div>
                <div className="mt-3">
                  <div className="text-xs text-slate-500 mb-1">Payment proof</div>
                  <ZoomImage fileId={s.file_id} className="max-h-72 w-auto rounded-lg border border-slate-200 bg-slate-50"
                    testid={`segment-proof-${s.segment_id}`} placeholder="No payment proof uploaded" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ label, value, tone }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`font-mono font-semibold mt-0.5 ${tone === "green" ? "text-emerald-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
